import { NICKNAME_CHANGE_LIMIT } from '@tomato/shared';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 소개·이용법·FAQ·About·약관·개인정보처리방침을 **한 페이지**에 모아둔다.
 * 푸터의 각 버튼은 이 페이지의 해당 섹션(#id)으로 이동하고, 여기서 부드럽게 스크롤한다.
 * (별도 페이지로 쪼개지 않는다 — 요청대로 한 곳에 두고 앵커로만 이동.)
 */
function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28 border-t border-ink-100 pt-8 first:border-0 first:pt-0">
      <h2 className="text-[20px] font-semibold tracking-tight text-ink-900">{title}</h2>
      <div className="mt-3 space-y-3 text-[14px] leading-relaxed text-ink-600">{children}</div>
    </section>
  );
}

function QA({ q, a }: { q: string; a: ReactNode }) {
  return (
    <div>
      <p className="font-medium text-ink-800">Q. {q}</p>
      <p className="mt-1 text-ink-600">A. {a}</p>
    </div>
  );
}

export function InfoScreen() {
  const { hash } = useLocation();

  // 해시가 있으면 해당 섹션으로 스크롤. 렌더 직후 레이아웃이 잡히도록 살짝 지연.
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="py-6">
      <h1 className="text-[26px] font-semibold tracking-tight text-ink-900">안내</h1>
      <p className="mt-1 text-[13px] text-ink-400">마지막 업데이트: 2026. 8. 12.</p>

      <div className="mt-6 space-y-8">
        <Section id="intro" title="헥토마토 소개">
          <p>
            <b className="text-ink-800">헥토마토</b>(토마토 마켓)는 헥토 사람들끼리 안 쓰는 물건을
            사고팔고 나누는 <b className="text-ink-800">사내 벼룩시장</b>이에요.
          </p>
          <p>
            회사 공식 시스템이 아니라, 있으면 편할 것 같아서 구성원이 개인적으로 만든 소소한 도구예요.
            서로 <b className="text-ink-800">활동명</b>으로만 오가고,{' '}
            <b className="text-ink-800">이메일은 아무에게도 보이지 않아요.</b> 조용히, 부담 없이 쓰시라고요.
          </p>
        </Section>

        <Section id="changelog" title="업데이트 이력">
          <div>
            <p className="font-medium text-ink-800">2026. 8. 12.</p>
            <ul className="mt-1.5 list-disc space-y-1 pl-5">
              <li>🎮 미니게임 — 🔍 오늘 토마토 찾기(하루 3개) · 🧺 토마토 따기(언제든, 지급은 하루 6판, 주간 리더보드)</li>
              <li>💬 댓글 이모지 반응 — 🍅👍❤️😂😮🙏 토글</li>
              <li>🌙 다크 모드 — 기기 설정 따라가고, 라이트/다크 고정도 가능</li>
              <li>📱 하단 탭바 · 사진 풀스크린 뷰어(쓸어내려 닫기) · 당겨서 새로고침</li>
              <li>🛒 장터 목록에 가격·거래 장소 표시, 카테고리 탭 상단 고정</li>
              <li>🔑 로그인 칸에서 저장된 패스키 자동완성(크롬) · 모바일 패스키 등록 안내</li>
              <li>✏️ 글 쓰다 나가면 한 번 확인 · 🔗 링크 공유 미리보기에 제목·내용</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-ink-800">2026. 7. 29.</p>
            <ul className="mt-1.5 list-disc space-y-1 pl-5">
              <li>🔮 오늘의 별자리 운세 — 매일 아침 ‘수다·정보’에 12별자리 순위·토마토 점수·럭키</li>
              <li>🛒 ‘같이 사요’ 카테고리 — 같이 살 사람 찾고 물건 추천(수다·정보)</li>
              <li>🔗 게시글 공유 — 링크 복사(로그아웃도 프리뷰로 열람), 공유로 가입하면 초대 적립</li>
              <li>✨ AI 작성 지원 — 장터 글, 대충 적으면 초안을 만들어드려요</li>
              <li>📨 카톡에서 열면 ‘기본 브라우저로 열기’ 버튼</li>
              <li>🤝 초대 링크로 오면 “○○님이 초대했어요” 표시</li>
              <li>🙈 비속어 자동 필터 · 활동명 변경 제한 등 소소한 정리</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-ink-800">2026. 7. 25.</p>
            <ul className="mt-1.5 list-disc space-y-1 pl-5">
              <li>🔑 패스키 로그인 — 등록한 기기는 지문·Face ID로 인증번호 없이</li>
              <li>📱 홈 화면에 추가(앱처럼 사용)</li>
              <li>✏️ 내 정보(활동명·계열사) 직접 수정</li>
              <li>🍅 토마토 포인트 — 방문·글·댓글·초대로 적립</li>
              <li>🤝 친구 초대 링크(초대로 가입하면 초대한 분께 100토마토)</li>
              <li>💬 관리자에게 제안하기</li>
              <li>카톡에서 열면 크롬·사파리로 열도록 안내</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-ink-800">2026. 7. 24.</p>
            <ul className="mt-1.5 list-disc space-y-1 pl-5">
              <li>🎉 베타 오픈</li>
              <li>회사 메일 아이디 로그인(인증번호)</li>
              <li>장터(팔기·사기·나눔·대여) · 라운지(수다·정보·취미·질문)</li>
              <li>찜 · 1:1 대화 · 약속 · 도착 알림</li>
              <li>텔레그램 알림 · 카톡 링크 미리보기</li>
            </ul>
          </div>
        </Section>

        <Section id="guide" title="처음 오셨나요? — 사이트 이용법">
          <ol className="list-decimal space-y-2 pl-5">
            <li>
              <b className="text-ink-800">로그인</b> — 회사 메일 아이디를 넣고 인증번호를 받아요. 처음이면
              바로 가입 화면으로 넘어가요. 활동명은 ‘🎲 이름 추천받기’로 손쉽게 정할 수 있어요.
            </li>
            <li>
              <b className="text-ink-800">더 빠르게</b> — 설정에서 패스키를 등록하면 다음부턴 지문·Face
              ID로 들어와요. 홈 화면에 추가하면 앱처럼 한 번에 열려요.
            </li>
            <li>
              <b className="text-ink-800">글쓰기</b> — 장터엔 물건을(팔기·사기·나눔·대여), 라운지엔
              이야기를(수다·정보·취미·질문) 올려요.
            </li>
            <li>
              <b className="text-ink-800">찜 → 대화 → 약속</b> — 마음에 드는 글을 찜하면 판매자에게 1:1로
              말 걸 수 있어요. 대화에서 만날 시간·장소를 정하고, 도착하면 상대에게 알림을 보낼 수 있어요.
            </li>
            <li>
              <b className="text-ink-800">알림</b> — 설정에서 텔레그램을 연결하면 내 글 댓글·새 대화를
              알려줘요.
            </li>
            <li>
              <b className="text-ink-800">토마토</b> — 방문·글·댓글·초대마다 쌓이는 재미 포인트예요.
            </li>
          </ol>
        </Section>

        <Section id="about" title="ABOUT">
          <p>
            <b className="text-ink-800">왜 만들었나요?</b> 사무실에 안 쓰는 물건은 많은데, 조용히
            주고받을 마땅한 곳이 없어서요.
          </p>
          <p>
            <b className="text-ink-800">어떻게 운영하나요?</b> 최소한의 정보만 받고, 익명(활동명)을
            우선하고, 무겁지 않게. 회사 리소스를 끌어들이지 않고 개인이 굴려요.
          </p>
          <p>
            <b className="text-ink-800">누가 운영하나요?</b> <b className="text-ink-800">d2dg</b>가
            개인적으로 만들고 운영해요. 공식 부서와는 무관해요.
          </p>
          <p>아직 실험 단계예요. 사람들이 써주면 계속 다듬어 갈게요.</p>

          <div className="pt-3">
            <h3 className="text-[15px] font-semibold text-ink-800">자주 묻는 질문</h3>
            <div className="mt-3 space-y-3">
              <QA q="제 이메일이 다른 사람에게 보이나요?" a="아니요. 다른 사람에겐 활동명만 보여요. 이메일은 본인만 볼 수 있어요." />
              <QA
                q="계열사는 공개되나요?"
                a="기본은 비공개예요. 설정에서 ‘공개’로 켠 경우에만, 본인 글에서 보여요."
              />
              <QA
                q="카톡에서 열었더니 패스키·설치가 안 돼요."
                a={
                  <>
                    카톡 안 브라우저(인앱)의 제약이에요. 오른쪽 위 메뉴에서{' '}
                    <b className="text-ink-800">‘크롬·사파리로 열기’</b> 하면 잘 돼요.
                  </>
                }
              />
              <QA
                q="활동명을 바꿀 수 있나요?"
                a={`설정 → 내 정보 → 수정에서 바꿀 수 있어요. 다만 자주 바꾸면 서로 알아보기 어려워서, ${NICKNAME_CHANGE_LIMIT.weeks}주에 ${NICKNAME_CHANGE_LIMIT.max}번까지만 바꿀 수 있어요.`}
              />
              <QA
                q="제가 찜한 걸 판매자가 아나요?"
                a="아니요. 누가 찜했는지는 판매자를 포함해 아무도 볼 수 없어요. 글에는 ‘N명이 찜함’ 숫자만 보여요."
              />
              <QA q="토마토는 어디에 쓰나요?" a="아직 쓸 데는 없어요. 그냥 재미로 모으는 중이에요 🍅" />
              <QA
                q="문제가 있거나 건의할 게 있어요."
                a={
                  <>
                    설정 맨 아래 <b className="text-ink-800">‘관리자에게 제안하기’</b>로 남겨주세요.
                  </>
                }
              />
            </div>
          </div>
        </Section>

        <Section id="terms" title="이용약관·운영규칙">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <b className="text-ink-800">이용 대상</b> — 헥토 구성원(@hecto.co.kr 메일 소유자)만 이용할
              수 있어요.
            </li>
            <li>
              <b className="text-ink-800">활동 방식</b> — 정해진 활동명으로 활동해요. 사칭, 도배·광고,
              불법·위험 물품 거래, 타인 비방·괴롭힘은 안 돼요.
            </li>
            <li>
              <b className="text-ink-800">거래 책임</b> — 거래는 당사자끼리 직접 하고 책임도 당사자에게
              있어요. 운영자는 중개하지 않으며 분쟁·손해에 책임지지 않아요.
            </li>
            <li>
              <b className="text-ink-800">제재</b> — 신고가 쌓이거나 규칙을 어기면 글이 삭제되거나 계정이
              비활성화될 수 있어요.
            </li>
            <li>
              <b className="text-ink-800">성격</b> — 이 서비스는 회사 공식 시스템이 아니에요. 기능은 예고
              없이 바뀌거나 중단될 수 있어요.
            </li>
          </ul>
        </Section>

        <Section id="privacy" title="개인정보 처리방침">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <b className="text-ink-800">무엇을 받나요</b> — 회사 이메일(로그인 식별용), 활동명, 선택
              입력한 계열사, 텔레그램을 연결한 경우 그 chat_id.
            </li>
            <li>
              <b className="text-ink-800">어떻게 쓰나요</b> — 로그인·알림 발송·최소한의 운영에만 써요.
              이메일은 다른 사용자에게 절대 보여주지 않아요.
            </li>
            <li>
              <b className="text-ink-800">인증번호</b> — 메일로 보낸 인증번호는 원문을 저장하지 않고,
              해시(암호화된 값)만 잠깐 보관해요.
            </li>
            <li>
              <b className="text-ink-800">보관·삭제</b> — 계정이 살아 있는 동안 보관해요. 탈퇴하면
              활동명·계열사·연동 정보·패스키와 이메일을 지우고, 작성한 글·댓글도 함께 삭제한 뒤
              계정을 닫아요. 탈퇴는 설정 맨 아래에서 직접 할 수 있어요.
            </li>
            <li>
              <b className="text-ink-800">맡기는 곳</b> — 데이터·이미지는 Supabase, 메일 발송은 Gmail,
              알림은 텔레그램을 통해요. 그 외 제3자에게 팔거나 넘기지 않아요.
            </li>
            <li>
              <b className="text-ink-800">문의</b> — 설정 → 관리자에게 제안하기.
            </li>
          </ul>
        </Section>
      </div>
    </div>
  );
}
